import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";

import MapboxMap, { Source, Layer, NavigationControl } from "react-map-gl/mapbox";
import Box from "@mui/material/Box";

import SideMenu from "./sideMenu";
import MapMenu from "./mapMenu";
import { BIN_TO_COLOR, MAP_STYLE } from "./constants";
import MunicipalityMarkers from "./markers/MunicipalityMarkers";
import PropertyMarkers from "./markers/PropertyMarkers";

import MapSearch from "../shared/mapSearch";
import useSidebarWidth from "../../shared/hooks/useSidebarWidth";
import {
  layerOpacityFactor,
  useLayerOpacities,
} from "../shared/useLayerOpacities";
import { useShow3dObjects } from "../shared/useShow3dObjects";
import PricesPopup from "./pricesPopup";
import PortfolioSelectorDialog from "./portfolioSelectorDialog";

import municipalitiesCentroidsJson from "../shared/municipalities_centroids.json";

import "mapbox-gl/dist/mapbox-gl.css";
import { useQuery } from "@tanstack/react-query";
import { getMunicipalityStats, getConfig } from "../../shared/api";
import { getVisibleMarkers } from "./helpers";
import { useClient } from "../../contexts/ClientContext";
import { Skeleton } from "@mui/material";

const initialViewState = {
  longitude: 23.7348324,
  latitude: 37.9755648,
  zoom: 10,
};

const BIN_TO_BASE_OPACITY = {
  Low: 0.5,
  Medium: 0.5,
  High: 0.5,
};

const COMPLETED_PORTFOLIO_STATUS = "completed";

const getPortfolioId = (portfolio) => portfolio?.id ?? null;

const isPortfolioSelectable = (portfolio) =>
  portfolio?.status === COMPLETED_PORTFOLIO_STATUS;

const getFirstSelectablePortfolioId = (portfolios = []) => {
  const firstSelectable = portfolios.find(isPortfolioSelectable);
  return getPortfolioId(firstSelectable);
};

const getInitialPortfolioId = ({ portfolios, defaultPortfolioId }) => {
  if (!Array.isArray(portfolios) || portfolios.length === 0) {
    return null;
  }

  const defaultPortfolio = portfolios.find(
    (portfolio) => getPortfolioId(portfolio) === defaultPortfolioId,
  );

  if (isPortfolioSelectable(defaultPortfolio)) {
    return defaultPortfolioId;
  }

  return getFirstSelectablePortfolioId(portfolios);
};

function buildIdMatchExpression(
  rows,
  mapRowToValue,
  defaultValue,
  { idType } = {},
) {
  if (!rows || rows.length === 0) return defaultValue; // ← important

  const expr = ["match", ["id"]];

  for (const row of rows) {
    const key =
      idType === "number"
        ? Number(row.id)
        : idType === "string"
          ? String(row.id)
          : row.id;
    expr.push(key, mapRowToValue(row));
  }

  expr.push(defaultValue);
  return expr;
}

function PortfolioMapInner({
  mapRef,
  openMenu,
  pricesPopupData,
  setPricesPopupData,
  setSelectedMunicipality,
  markerInfo,
  setMarkerInfo,
  activeTab,
  setActiveTab,
  currentProperties,
  clientInfo,
  selectedPortfolioId,
  onPortfolioChange,
  configData,
}) {
  const [activeRisk, setActiveRisk] = useState("fire");
  const [activeMapStyle, setActiveMapStyle] = useState(MAP_STYLE.street);
  const [hoveredId, setHoveredId] = useState(null);
  const [visibleMunicipalityValueType, setVisibleMunicipalityValueType] =
    useState("capital");
  const [zoom, setZoom] = useState(initialViewState.zoom);
  const [show3DMap, setShow3DMap] = useState(false);
  // 3D buildings come from the Mapbox Standard style (shown by default).
  const [show3DBuildings, setShow3DBuildings] = useState(true);
  const { layerOpacities, setLayerOpacity } = useLayerOpacities();

  // Satellite (Standard Satellite) has no 3D buildings, so force them off there
  // while keeping the user's toggle preference for when they return to street.
  useShow3dObjects(
    mapRef,
    show3DBuildings && activeMapStyle !== MAP_STYLE.satellite,
  );

  const handleZoom = useCallback((e) => {
    setZoom(e.viewState.zoom);
  }, []);

  const setActiveMapStyleSafe = useCallback(
    (styleUrl) => {
      const map = mapRef.current?.getMap();
      // Clear fog so the transition doesn't query a missing fog state
      // fixes marker bug
      try {
        map?.setFog(null);
      } catch {}
      setActiveMapStyle(styleUrl);
    },
    [mapRef],
  );

  // Handle hover event
  const handleMouseMove = useCallback(
    (e) => {
      if (!e.features?.length) return;

      const feature = e.features[0];
      const id = feature.id; // important: your tileset must have unique feature ids

      if (id !== hoveredId) {
        const map = mapRef.current.getMap();

        // Reset previous hover
        if (hoveredId !== null) {
          map.setFeatureState(
            {
              source: "municipalities-source",
              sourceLayer: "municipalities",
              id: hoveredId,
            },
            { hover: false },
          );
        }

        // Set new hover
        map.setFeatureState(
          {
            source: "municipalities-source",
            sourceLayer: "municipalities",
            id,
          },
          { hover: true },
        );

        setHoveredId(id);
      }
    },
    [hoveredId, mapRef],
  );

  // Reset highlight when leaving layer
  const handleMouseLeave = useCallback(() => {
    const map = mapRef.current.getMap();
    if (hoveredId !== null) {
      map.setFeatureState(
        {
          source: "municipalities-source",
          sourceLayer: "municipalities",
          id: hoveredId,
        },
        { hover: false },
      );
    }
    setHoveredId(null);
  }, [hoveredId, mapRef]);

  const onLocationSelect = useCallback(
    ({ lat, lng }) => {
      mapRef.current?.getMap()?.flyTo({ center: [lng, lat] });
    },
    [mapRef],
  );

  const { data: municipalitiesData } = useQuery({
    queryKey: ["municipality-stats", clientInfo?.clientId, selectedPortfolioId],
    queryFn: () =>
      getMunicipalityStats({
        clientId: clientInfo?.clientId,
        portfolioId: selectedPortfolioId,
      }),
    staleTime: 5 * 60 * 1000, // 5 minutes - data is considered fresh for 5 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes - keep in cache for 30 minutes
    enabled: !!clientInfo?.clientId && !!selectedPortfolioId,
  });

  const stats = useMemo(() => {
    return municipalitiesData?.items ?? [];
  }, [municipalitiesData]);

  const fillColor = useMemo(() => {
    return buildIdMatchExpression(
      stats,
      (row) => {
        const bin =
          activeRisk === "fire"
            ? row.fire_bin
            : activeRisk === "flood"
              ? row.flood_bin
              : row.earthquake_bin;

        return BIN_TO_COLOR[bin] || "#dadee0";
      },
      "#dadee0",
      { idType: "number" },
    );
  }, [stats, activeRisk]);

  const baseOpacityExpr = useMemo(() => {
    return buildIdMatchExpression(
      stats,
      (row) => {
        const bin =
          activeRisk === "fire"
            ? row.fire_bin
            : activeRisk === "flood"
              ? row.flood_bin
              : row.earthquake_bin;

        return BIN_TO_BASE_OPACITY[bin] || 0.5;
      },
      0.5,
      { idType: "number" },
    );
  }, [stats, activeRisk]);

  const municipalityLayerOpacity = layerOpacityFactor(
    activeRisk,
    layerOpacities[activeRisk],
  );

  const fillOpacity = useMemo(
    () => [
      "*",
      municipalityLayerOpacity,
      [
        "case",
        ["boolean", ["feature-state", "hover"], false],
        0.7,
        baseOpacityExpr,
      ],
    ],
    [baseOpacityExpr, municipalityLayerOpacity],
  );

  const municipalities = useMemo(() => {
    if (!stats || stats.length === 0) return [];

    // Build a lookup map for quick access
    const statsMap = new Map(stats.map((s) => [s.id, s]));

    // Merge both based on id
    return municipalitiesCentroidsJson.map((m) => ({
      ...m,
      ...(statsMap.get(m.id) || {}), // merge stats if found
    }));
  }, [stats]);

  // Memoize the filtered municipalities separately to avoid recalculating on zoom changes
  const filteredMunicipalities = useMemo(() => {
    return municipalities.filter((mun) => !!mun.properties_count);
  }, [municipalities]);

  // Only recalculate visible markers when municipalities change, not on every zoom
  const markers = useMemo(() => {
    return getVisibleMarkers(filteredMunicipalities, zoom);
  }, [filteredMunicipalities, zoom]);

  const onMapClick = useCallback(
    (e) => {
      const { lng, lat } = e.lngLat;

      const features = e.features || [];

      if (!features.length) {
        return;
      }

      const properties = features[0].properties;

      if (!properties) {
        return;
      }

      const municipality = municipalities.find(
        (mun) => mun.id === properties.id,
      );

      if (!municipality) {
        return;
      }

      openMenu(true);
      setActiveTab(0);
      setSelectedMunicipality({
        id: municipality.id,
        name: municipality["name:en"],
      });
      setPricesPopupData({
        ...municipality,
        open: true,
        name: municipality.municipality,
        capitalAtRisk: municipality.total_capital_at_risk,
        contractPrice: municipality.total_contract_price,
        totalProperties: municipality.properties_count,
        lng,
        lat,
      });
    },
    [
      municipalities,
      openMenu,
      setPricesPopupData,
      setSelectedMunicipality,
      setActiveTab,
    ],
  );

  return (
    <div
      style={{
        width: "100%",
        height: "calc(100vh - 64px)",
        position: "relative",
      }}
    >
      <MapSearch
        onLocationSelect={onLocationSelect}
        placeholderKey="portfolioMap.mapSearchPlaceholder"
      />

      <PortfolioSelectorDialog
        selectedPortfolioId={selectedPortfolioId}
        onPortfolioChange={onPortfolioChange}
        configData={configData}
      />

      <MapboxMap
        ref={mapRef}
        style={{ width: "100%", height: "100%" }}
        mapStyle={activeMapStyle}
        projection="mercator"
        terrain={
          show3DMap ? { source: "mapbox-dem", exaggeration: 1.2 } : undefined
        }
        initialViewState={initialViewState}
        mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN}
        onClick={onMapClick}
        interactiveLayerIds={["municipalities-layer-fill"]}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onMove={handleZoom}
        maxPitch={85}
      >
        <NavigationControl position="top-right" />

        {/* --- 3D TERRAIN DEM SOURCE --- */}
        <Source
          id="mapbox-dem"
          type="raster-dem"
          url="mapbox://mapbox.mapbox-terrain-dem-v1"
          tileSize={512}
          maxzoom={14}
        />

        {pricesPopupData.open && (
          <PricesPopup
            {...pricesPopupData}
            onClose={(prev) => setPricesPopupData({ ...prev, open: false })}
          />
        )}

        {/* Municipality markers - only show on municipalities tab */}
        {activeTab === 0 && (
          <MunicipalityMarkers
            markers={markers}
            visibleMunicipalityValueType={visibleMunicipalityValueType}
          />
        )}

        {/* Property markers */}
        {currentProperties?.length > 0 && (
          <PropertyMarkers
            properties={currentProperties}
            markerInfo={markerInfo}
            mapRef={mapRef}
          />
        )}

        <Source
          id="municipalities-source"
          url="mapbox://georgemouts.55l8r190"
          type="vector"
          promoteId={{ municipalities: "id" }}
        >
          <Layer
            id="municipalities-layer-fill"
            type="fill"
            source-layer="municipalities"
            paint={{
              "fill-color": fillColor, // <-- driven by fetched data via id match
              "fill-opacity": fillOpacity, // <-- hover override + per-id opacity
            }}
          />

          <Layer
            id="municipalities-layer-line"
            type="line"
            paint={{ "line-color": "black", "line-width": 1 }}
            source-layer="municipalities"
          />
        </Source>
      </MapboxMap>

      <MapMenu
        setActiveRisk={setActiveRisk}
        activeRisk={activeRisk}
        setActiveMapStyle={setActiveMapStyleSafe}
        activeMapStyle={activeMapStyle}
        visibleMunicipalityValueType={visibleMunicipalityValueType}
        setVisibleMunicipalityValueType={setVisibleMunicipalityValueType}
        layerOpacities={layerOpacities}
        setLayerOpacity={setLayerOpacity}
        show3DMap={show3DMap}
        setShow3DMap={setShow3DMap}
        show3DBuildings={show3DBuildings}
        setShow3DBuildings={setShow3DBuildings}
      />
    </div>
  );
}

function PortfolioMap({ configData }) {
  const { clientInfo } = useClient();
  const mapRef = useRef();

  const { width: drawerWidth, setWidth: setDrawerWidth } = useSidebarWidth({
    storageKey: "artemis.sidebarWidth.portfolioMap",
    minWidth: 460,
  });

  const portfolios = useMemo(
    () => configData?.portfolios?.available ?? [],
    [configData?.portfolios?.available],
  );
  const defaultPortfolioId = configData?.portfolios?.default ?? null;

  const [menuOpen, setMenuOpen] = React.useState(true);
  const [activeTab, setActiveTab] = useState(0);

  const [markerInfo, setMarkerInfo] = useState(null);
  const [currentProperties, setCurrentProperties] = useState([]);

  const [pricesPopupData, setPricesPopupData] = useState({ open: false });
  const [selectedMunicipality, setSelectedMunicipality] = useState(null);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(() =>
    getInitialPortfolioId({
      portfolios,
      defaultPortfolioId,
    }),
  );

  useEffect(() => {
    setSelectedPortfolioId((previousSelectedPortfolioId) => {
      const hasPreviousSelection = portfolios.some(
        (portfolio) =>
          getPortfolioId(portfolio) === previousSelectedPortfolioId &&
          isPortfolioSelectable(portfolio),
      );

      if (hasPreviousSelection) {
        return previousSelectedPortfolioId;
      }

      return getInitialPortfolioId({
        portfolios,
        defaultPortfolioId,
      });
    });
  }, [portfolios, defaultPortfolioId]);

  const handlePortfolioChange = useCallback((nextPortfolioId) => {
    setSelectedPortfolioId(nextPortfolioId);
    setSelectedMunicipality(null);
    setMarkerInfo(null);
    setCurrentProperties([]);
    setPricesPopupData({ open: false });
  }, []);

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const openMenu = () => {
    setMenuOpen(true);
  };

  useEffect(() => {
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.getMap().resize();
      }
    }, 300); // matches your transition duration
  }, [menuOpen, drawerWidth]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${menuOpen ? drawerWidth : 0}px)` },
          transition: "margin 0.3s",
          marginRight: menuOpen ? `${drawerWidth}px` : 0,
        }}
      >
        <PortfolioMapInner
          mapRef={mapRef}
          markerInfo={markerInfo}
          setMarkerInfo={setMarkerInfo}
          pricesPopupData={pricesPopupData}
          setPricesPopupData={setPricesPopupData}
          setSelectedMunicipality={setSelectedMunicipality}
          openMenu={openMenu}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          currentProperties={currentProperties}
          clientInfo={clientInfo}
          selectedPortfolioId={selectedPortfolioId}
          onPortfolioChange={handlePortfolioChange}
          configData={configData}
        />
      </Box>

      <SideMenu
        open={menuOpen}
        handleToggle={handleMenuToggle}
        mapRef={mapRef}
        setMarkerInfo={setMarkerInfo}
        selectedMunicipality={selectedMunicipality}
        setSelectedMunicipality={setSelectedMunicipality}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        setCurrentProperties={setCurrentProperties}
        selectedPortfolioId={selectedPortfolioId}
        width={drawerWidth}
        onResize={setDrawerWidth}
      />
    </Box>
  );
}

const PortfolioMapWithConfig = () => {
  const { clientInfo } = useClient();
  const { data: configData, isLoading: isConfigLoading } = useQuery({
    queryKey: ["config", clientInfo?.clientId],
    queryFn: () => getConfig({ clientId: clientInfo?.clientId }),
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: !!clientInfo?.clientId,
  });

  // Show skeletons while config is loading
  if (isConfigLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%",
          height: "calc(100vh - 64px)",
          position: "relative",
        }}
      >
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
    );
  }

  return <PortfolioMap configData={configData} />;
};

export default PortfolioMapWithConfig;
